import { DeleteOutlined } from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'

import { MultiAssignmentFieldCell } from '@/Containers/AssignmentSection/AssignmentContainer/Components/MultiAssignmentFieldCell'
import { cx } from '@/Ship/Helpers/cssClass'

import { userUuidOf, vehicleFilterFor } from '../Helpers/driverVehicle'
import { LoadDriverCarrierTask } from '../Tasks/LoadDriverCarrierTask'

import type { MultiShiftDrawerStore, ShiftRow } from '../Stores/MultiShiftDrawerStore'

import styles from './MultiShiftDrawer.module.css'

interface Props {
    store: MultiShiftDrawerStore
    row: ShiftRow
    index: number
}

const driverLabel = (item: any) => item?.fullName || `${item?.lastName || ''} ${item?.firstName || ''}`.trim() || '—'
const vehicleLabel = (item: any) => item?.licensePlate || item?.registrationNumber || item?.title || '—'

/**
 * Рядок «водій + ТЗ» вікна множинного створення змін. Каскад як у формі зміни: ТЗ —
 * лише перевізника водія (власному водію — власних перевізників), поки водія не обрано,
 * ТЗ заблокований; зміна водія скидає ТЗ (стор), а комірка ТЗ перемонтовується порожньою.
 */
export const MultiShiftRow = observer(({ store, row, index }: Props) => {
    const number = index + 1
    const driverUuid = userUuidOf(row.driver)

    // Перевізника водія беремо з його картки (у значенні select-а його немає)
    const [driverCarrier, setDriverCarrier] = useState<any | undefined>(undefined)
    useEffect(() => {
        let cancelled = false
        setDriverCarrier(undefined)
        if (driverUuid) {
            LoadDriverCarrierTask(driverUuid)
                .then((carrier) => {
                    if (!cancelled) setDriverCarrier(carrier)
                })
                .catch(() => {
                    // Без перевізника фільтр лишається базовим; бекенд усе одно перевірить пару
                    if (!cancelled) setDriverCarrier(undefined)
                })
        }
        return () => {
            cancelled = true
        }
    }, [driverUuid])

    const driverDef = useMemo(
        () => ({
            type: 'select',
            resource: 'Setting.User',
            inputProps: {
                filter: { filterRoles: { op: 'like', value: 'ROLE_DRIVER' }, status: 'active' },
                'aria-label': `Водій, рядок ${number}`,
                responseLabel: driverLabel,
            },
        }),
        [number]
    )

    const vehicleFilterKey = JSON.stringify(vehicleFilterFor(driverCarrier))
    const vehicleDef = useMemo(
        () => ({
            type: 'select',
            resource: 'Transport.Vehicle',
            inputProps: {
                filter: JSON.parse(vehicleFilterKey),
                disabled: !driverUuid,
                'aria-label': `Транспортний засіб, рядок ${number}`,
                responseLabel: vehicleLabel,
            },
        }),
        [vehicleFilterKey, driverUuid, number]
    )

    return (
        <div className={cx(styles, 'row')} role="listitem">
            <span className={cx(styles, 'rowNumber')}>{number}</span>
            <div className={cx(styles, 'rowCell')} data-label="Водій">
                <MultiAssignmentFieldCell
                    fieldName="driver"
                    fieldDef={driverDef}
                    initialValue={row.driver}
                    onChange={(value) => store.setRowDriver(row.id, value)}
                />
            </div>
            <div className={cx(styles, 'rowCell')} data-label="Транспортний засіб">
                <MultiAssignmentFieldCell
                    key={`vehicle-${row.id}-${driverUuid ?? ''}`}
                    fieldName="vehicle"
                    fieldDef={vehicleDef}
                    initialValue={row.vehicle}
                    onChange={(value) => store.setRowVehicle(row.id, value)}
                    disabled={!driverUuid}
                />
            </div>
            <Tooltip title={store.rows.length > 1 ? 'Видалити рядок' : undefined}>
                <Button
                    type="text"
                    className={cx(styles, 'rowRemove')}
                    icon={<DeleteOutlined />}
                    aria-label={`Видалити рядок ${number}`}
                    disabled={store.rows.length <= 1}
                    onClick={() => store.removeRow(row.id)}
                />
            </Tooltip>
        </div>
    )
})
